"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { flushSync } from "react-dom"
import { useSelector, useDispatch, useStore } from "react-redux"
import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { logoutUser } from "../../store/slices/authSlice"
import { clearClients, clearClientsOrgResolutionCache } from "../../store/slices/clientsSlice"
import { clearUserProfile } from "../../store/slices/userSlice"
import { resetTopicsOnLogout } from "../../store/slices/topicsSlice"
import { resetCampaignOnLogout } from "../../store/slices/campaignSlice"
import { resetCommentsOnLogout } from "../../store/slices/commentsSlice"
import { resetApproverOnLogout } from "../../store/slices/approverSlice"
import { resetEventsOnLogout } from "../../store/slices/eventsSlice"
import { resetHolidaysOnLogout } from "../../store/slices/holidaysSlice"
import { resetContentPillarsOnLogout } from "../../store/slices/contentPillarsSlice"
import { resetOnLogout as resetSocialNetworkTypesOnLogout } from "../../store/slices/socialNetworkTypesSlice"
import { resetOnLogout as resetSocialNetworkAccountsOnLogout } from "../../store/slices/socialNetworkAccountsSlice"
import { resetOnLogout as resetCitiesAiOnLogout } from "../../store/slices/citiesAiSlice"
import { clearInboxThreads } from "../../store/slices/inboxThreadsSlice"
import { clearInboxSummary } from "../../store/slices/inboxSummarySlice"
import { clearClientNotes } from "../../store/slices/clientNotesSlice"
import { clearClientNoteTypes } from "../../store/slices/clientNoteTypesSlice"
import { resetPublisherOnLogout } from "../../store/slices/publisherSlice"
import { resetTargetAudienceOnLogout } from "../../store/slices/targetAudienceSlice"
import ComingSoonPill from "../common/coming-soon/ComingSoonPill"
import { fetchMediaStats } from "../../store/slices/mediaStatsSlice"
import { clientHasUploadedMedia } from "../../lib/mediaStatsApi"
import { fetchCollaborators } from "../../store/slices/collaboratorsSlice"
import { fetchOrganizationsRole, resetOrganizationRole } from "../../store/slices/organizationRoleSlice"
import { resetOrganizationsState } from "../../store/slices/settingOrganizationSlice"
import {
  canAccessExecutiveDashboard,
  getRoleLabel,
  getSidebarRoleModifier,
  getUserDisplayRole,
  isOrganizationRolePending,
  normalizeRoleKey,
} from "../../lib/utils/userRoleDisplay"
import ConfirmModal from "../common/confirm-modal/ConfirmModal"
import HoverTooltip from "../common/tooltip/HoverTooltip"
import { profileAvatarDisplayUrl } from "../../lib/utils/userProfileAvatar"
import { prefetchRoute } from "../../routes/lazyPages"
import "./Sidebar.css"

function clientHasAssetLibrary(store, clientId) {
  const statsByClient = store.getState().mediaStats?.statsByClient
  const userState = store.getState().user ?? {}
  const upload = store.getState().assetUpload ?? {}
  const mediasBelongToClient =
    userState.mediasClientId != null &&
    String(userState.mediasClientId) === String(clientId)
  const organizingForClient =
    upload.clientId != null &&
    String(upload.clientId) === String(clientId) &&
    (upload.phase === "uploading" || upload.phase === "processing" || upload.phase === "complete")
  return (
    clientHasUploadedMedia(statsByClient, clientId) ||
    (mediasBelongToClient && (userState.medias || []).length > 0) ||
    organizingForClient
  )
}

export const Sidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const store = useStore()

  const getActiveTabFromPath = () => {
    const path = location.pathname
    if (path.includes("/multi-property-dashboard")) return "Multi-Property Dashboard"
    if (path.includes("/dashboard")) return "Dashboard"
    if (path.includes("/calendar")) return "Calendar"
    if (path.includes("/settings") || path.includes("/organization-settings")) return "Settings"
    if (
      path.includes("/medialibrary") ||
      path.includes("/media-library-details") ||
      path.includes("/media-library/")
    ) {
      return "Media Library"
    }
    if (
      path.includes("/aiassetmanager") ||
      path.includes("/ai-asset-manager-details") ||
      path.includes("/ai-asset-manager/")
    ) {
      return "AI Asset Manager"
    }
    if (path.includes("/ai-community-inbox") || path.includes("/reputation")) return "Community AI Inbox"
    if (path.includes("/ugc")) return "User Generated Content"
    if (path.includes("/ai-audits")) return "AI Audits"
    if (path.includes("/influencer-management")) return "Influencer Management"
    return "Calendar"
  }

  const activeTab = useMemo(() => getActiveTabFromPath(), [location.pathname])
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth <= 1400)
  const [showProfilePopover, setShowProfilePopover] = useState(false)
  const profilePopoverCloseTimerRef = useRef(null)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [logoutInProgress, setLogoutInProgress] = useState(false)

  const clearProfilePopoverCloseTimer = () => {
    if (profilePopoverCloseTimerRef.current) {
      clearTimeout(profilePopoverCloseTimerRef.current)
      profilePopoverCloseTimerRef.current = null
    }
  }

  const scheduleProfilePopoverClose = () => {
    clearProfilePopoverCloseTimer()
    profilePopoverCloseTimerRef.current = setTimeout(() => {
      profilePopoverCloseTimerRef.current = null
      setShowProfilePopover(false)
    }, 180)
  }

  const openProfilePopover = () => {
    clearProfilePopoverCloseTimer()
    setShowProfilePopover(true)
  }

  useEffect(() => () => clearProfilePopoverCloseTimer(), [])

  useEffect(() => {
    if (!isCollapsed) {
      clearProfilePopoverCloseTimer()
      setShowProfilePopover(false)
    }
  }, [isCollapsed])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1400) {
        setIsCollapsed(true)
      } else {
        setIsCollapsed(false)
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const { profile: userProfile } = useSelector((state) => state.user)
  const { selectedClient } = useSelector((state) => state.clients)
  const organizationsListStatus = useSelector(
    (state) => state.organizations?.organizationsListStatus,
  )
  const currentUserRole = useSelector((state) => state.collaborators?.currentUserRole)
  const collaboratorRows = useSelector((state) => state.collaborators?.collaborators)
  const {
    organizations: orgRoleOrganizations,
    userOrgRole,
    organizationsStatus,
    userOrgRoleStatus,
  } = useSelector((state) => state.organizationRole || {})

  const orgRolePending = useMemo(
    () =>
      isOrganizationRolePending({
        hasUserId: Boolean(userProfile?.id),
        organizationsStatus,
        userOrgRoleStatus,
        organizationCount: orgRoleOrganizations?.length ?? 0,
      }),
    [userProfile?.id, organizationsStatus, userOrgRoleStatus, orgRoleOrganizations?.length],
  )

  const derivedRole = useMemo(() => {
    const fromDisplay = getUserDisplayRole({
      organizationRole: userOrgRole?.role,
      collaboratorRows,
      propertyRole: currentUserRole,
      orgRolePending,
    })
    if (fromDisplay) return fromDisplay
    if (orgRolePending) return null
    return currentUserRole ? normalizeRoleKey(currentUserRole) : null
  }, [collaboratorRows, userOrgRole?.role, currentUserRole, orgRolePending])
  const roleLabel = useMemo(() => (derivedRole ? getRoleLabel(derivedRole) : ""), [derivedRole])
  const showExecutiveDashboard = useMemo(
    () => !orgRolePending && canAccessExecutiveDashboard(userOrgRole?.role),
    [orgRolePending, userOrgRole?.role],
  )

  const profileEmailLine = useMemo(
    () => userProfile?.email || userProfile?.username || "—",
    [userProfile?.email, userProfile?.username],
  )

  const getCollaboratorRole = () => currentUserRole?.replace("ROLE_", "")

  const collaboratorRole = getCollaboratorRole()
  const isModerator = collaboratorRole === "MODERATOR"
  const MODERATOR_BLOCKED_NAV = new Set([
    "UGC",
    "User Generated Content",
    "Influencer Management",
    "Ads",
    "AI Asset Manager",
    "Media Library",
    "AI Audits",
  ])
  const isNavDisabledForModerator = (label) =>
    isModerator && MODERATOR_BLOCKED_NAV.has(label)

  const lastOrgRoleUserRef = useRef(null)

  useEffect(() => {
    if (!userProfile?.id) {
      lastOrgRoleUserRef.current = null
      return
    }
    if (organizationsListStatus !== "succeeded") return
    if (lastOrgRoleUserRef.current === userProfile.id) return
    lastOrgRoleUserRef.current = userProfile.id
    dispatch(fetchOrganizationsRole({ userId: userProfile.id }))
  }, [dispatch, userProfile?.id, organizationsListStatus])

  useEffect(() => {
    if (!userProfile?.id || !selectedClient) return
    const clientId = selectedClient.id ?? selectedClient.clientId
    if (clientId) {
      dispatch(fetchCollaborators({ clientId, userId: userProfile.id }))
    }
  }, [dispatch, userProfile?.id, selectedClient])

  useEffect(() => {
    const checkRouteOnRefresh = async () => {
      const clientId = selectedClient?.id ?? selectedClient?.clientId
      if (!clientId) return

      const path = location.pathname

      if (path === "/media-library-details" || path.startsWith("/media-library/")) {
        navigate("/medialibrary", { replace: true })
        return
      }

      if (path === "/ai-asset-manager-details" || path.startsWith("/ai-asset-manager/category")) {
        await dispatch(fetchMediaStats({ clientId, force: true }))
        if (!clientHasAssetLibrary(store, clientId)) {
          navigate("/aiassetmanager", { replace: true })
        }
      }
    }

    checkRouteOnRefresh()
  }, [selectedClient?.id, selectedClient?.clientId, dispatch, location.pathname, navigate])

  const handleMediaLibraryNavigation = () => {
    navigate("/medialibrary")
  }

  const handleAssetManagerNavigation = async () => {
    const clientId = selectedClient?.id ?? selectedClient?.clientId

    if (!clientId) {
      navigate("/aiassetmanager")
      return
    }
    await dispatch(fetchMediaStats({ clientId, force: true }))
    navigate(clientHasAssetLibrary(store, clientId) ? "/ai-asset-manager-details" : "/aiassetmanager")
  }

  const menuItems = useMemo(() => {
    const items = [
      { icon: <img src="/assets/icons/common/line-chart.svg" className="nav-icon" alt="" />, label: "Dashboard", to: "/dashboard" },
      ...(showExecutiveDashboard
        ? [
            {
              icon: <img src="/assets/icons/common/line-chart.svg" className="nav-icon" alt="" />,
              label: "Multi-Property Dashboard",
              to: "/multi-property-dashboard",
            },
          ]
        : []),
      { icon: <img src="/assets/icons/common/date_icon.svg" className="nav-icon" alt="" />, label: "Calendar", to: "/calendar" },
    { icon: <img src="/assets/icons/common/UGC.svg" className="nav-icon" alt="" />, label: "User Generated Content", to: "/ugc" },
    { icon: <img src="/assets/illustrations/Reputation.svg" className="nav-icon" alt="" />, label: "Community AI Inbox", to: "/ai-community-inbox" },
    {
      icon: <img src="/assets/icons/common/posts.svg" className="nav-icon" alt="" />,
      label: "Influencer Management",
      navClickDisabled: true,
    },
    {
      icon: <img src="/assets/icons/common/calendar.svg" className="nav-icon" alt="" />,
      label: "Ads",
      comingSoon: true,
    },
    { icon: <img src="/assets/icons/common/asset.svg" className="nav-icon" alt="" />, label: "AI Asset Manager", assetNav: true },
    { icon: <img src="/assets/icons/common/media-library-nav.svg" className="nav-icon" alt="" />, label: "Media Library", mediaLibraryNav: true },
      { icon: <img src="/assets/icons/common/ai-audit.svg" className="nav-icon" alt="" />, label: "AI Audits", to: "/ai-audits" },
    ]
    return items
  }, [showExecutiveDashboard])

  const navItemClass = (label, { comingSoon, navClickDisabled } = {}) => {
    const disabled =
      comingSoon || navClickDisabled || isNavDisabledForModerator(label)
    const comingSoonHost = comingSoon ? "coming-soon-host coming-soon-host--highlight-nav" : ""
    return `nav-item ${activeTab === label ? "active" : ""} ${disabled ? "disabled" : ""} ${comingSoonHost}`.trim()
  }

  const wrapCollapsedNavLabel = (label, node) => (
    <HoverTooltip
      content={isCollapsed ? label : ""}
      placement="right"
      strategy="portal"
      compact
      wrapClassName="sidebar-nav-tooltip-target"
    >
      {node}
    </HoverTooltip>
  )

  const performLogout = async () => {
    setLogoutInProgress(true)
    try {
      sessionStorage.clear()
      localStorage.removeItem("selectedClient")

      dispatch(clearClients())
      clearClientsOrgResolutionCache()
      dispatch(resetOrganizationRole())
      dispatch(resetOrganizationsState())
      dispatch(clearUserProfile())
      dispatch(resetTopicsOnLogout())
      dispatch(resetCampaignOnLogout())
      dispatch(resetCommentsOnLogout())
      dispatch(resetApproverOnLogout())
      dispatch(resetEventsOnLogout())
      dispatch(resetHolidaysOnLogout())
      dispatch(resetContentPillarsOnLogout())
      dispatch(resetSocialNetworkTypesOnLogout())
      dispatch(resetSocialNetworkAccountsOnLogout())
      dispatch(resetCitiesAiOnLogout())
      dispatch(clearInboxThreads())
      dispatch(clearInboxSummary())
      dispatch(clearClientNotes())
      dispatch(clearClientNoteTypes())
      dispatch(resetPublisherOnLogout())
      dispatch(resetTargetAudienceOnLogout())

      await dispatch(logoutUser()).unwrap()
      setShowLogoutConfirm(false)
      window.location.href = "/login"
    } catch (error) {
      console.error("Logout error:", error)
      setShowLogoutConfirm(false)
      window.location.href = "/login"
    } finally {
      setLogoutInProgress(false)
    }
  }

  const displayName =
    userProfile?.fullName ||
    (userProfile?.username ? userProfile.username.split("@")[0] : "User")

  const profileAvatarSrc =
    profileAvatarDisplayUrl(userProfile) || "/assets/icons/common/UserIcon.svg"

  return (
    <>
      <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const navBody = (
              <>
                {item.icon}
                {!isCollapsed && <span className="nav-label">{item.label}</span>}
                {!isCollapsed && item.comingSoon && (
                  <ComingSoonPill placement="inline-end" />
                )}
              </>
            )

            if (item.comingSoon || item.navClickDisabled) {
              return (
                <div key={item.label} className={navItemClass(item.label, item)}>
                  {wrapCollapsedNavLabel(item.label, navBody)}
                </div>
              )
            }

            if (item.mediaLibraryNav) {
              return (
                <div
                  key={item.label}
                  className={navItemClass(item.label, item)}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (isNavDisabledForModerator(item.label)) return
                    handleMediaLibraryNavigation()
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return
                    e.preventDefault()
                    if (isNavDisabledForModerator(item.label)) return
                    handleMediaLibraryNavigation()
                  }}
                >
                  {wrapCollapsedNavLabel(item.label, navBody)}
                </div>
              )
            }

            if (item.assetNav) {
              return (
                <div
                  key={item.label}
                  className={navItemClass(item.label, item)}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (isNavDisabledForModerator(item.label)) return
                    handleAssetManagerNavigation()
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return
                    e.preventDefault()
                    if (isNavDisabledForModerator(item.label)) return
                    handleAssetManagerNavigation()
                  }}
                >
                  {wrapCollapsedNavLabel(item.label, navBody)}
                </div>
              )
            }

            return (
              <NavLink
                key={item.label}
                to={item.to}
                end
                className={() => navItemClass(item.label, item)}
                onMouseEnter={() => prefetchRoute(item.to)}
                onFocus={() => prefetchRoute(item.to)}
                onClick={(e) => {
                  const blocked = isNavDisabledForModerator(item.label)
                  if (blocked) {
                    e.preventDefault()
                    return
                  }
                  e.preventDefault()
                  flushSync(() => {
                    navigate(item.to)
                  })
                }}
              >
                {wrapCollapsedNavLabel(item.label, navBody)}
              </NavLink>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <NavLink
            to="/settings"
            className={`nav-item settings-item ${activeTab === "Settings" ? "active" : ""}`}
            onMouseEnter={() => prefetchRoute("/settings")}
            onFocus={() => prefetchRoute("/settings")}
            onClick={(e) => {
              const blocked = isNavDisabledForModerator("Settings")
              if (blocked) {
                e.preventDefault()
              }
            }}
          >
            {wrapCollapsedNavLabel(
              "Settings",
              <>
                <img src="/assets/icons/common/settings.svg" alt="settings" className="nav-icon" />
                {!isCollapsed && <span className="nav-label">Settings</span>}
              </>,
            )}
          </NavLink>

          <div
            className={`sidebar-profile-popout-host${isCollapsed ? " sidebar-profile-popout-host--collapsed" : ""}`}
            onMouseEnter={() => isCollapsed && openProfilePopover()}
            onMouseLeave={() => isCollapsed && scheduleProfilePopoverClose()}
          >
            <div
              className="user-profile"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!isCollapsed) setShowLogoutConfirm(true)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  if (!isCollapsed) setShowLogoutConfirm(true)
                }
              }}
            >
              <img
                src={profileAvatarSrc}
                alt=""
                className={`nav-icon${userProfile?.avatar ? " sidebar-user-avatar" : ""}`}
              />
              {!isCollapsed && (
                <span className="user-name user-name-stack">
                  <span className="user-name-text">{displayName}</span>
                  <span className="sidebar-expanded-user-email" title={profileEmailLine}>
                    {profileEmailLine}
                  </span>
                  {roleLabel ? (
                    <span
                      className={`sidebar-user-role sidebar-user-role--${getSidebarRoleModifier(derivedRole)}`}
                      title={roleLabel}
                    >
                      {roleLabel}
                    </span>
                  ) : null}
                  <div className="clicktoLogogout">Logout</div>
                </span>
              )}
            </div>

            {isCollapsed && showProfilePopover && (
              <div
                className="sidebar-profile-popover"
                role="dialog"
                aria-label="Account"
                onMouseEnter={openProfilePopover}
                onMouseLeave={scheduleProfilePopoverClose}
              >
                <div className="sidebar-profile-popover__header">
                  <img
                    src={profileAvatarSrc}
                    alt=""
                    className={`sidebar-profile-popover__profile-icon${userProfile?.avatar ? " sidebar-user-avatar" : ""}`}
                    aria-hidden="true"
                  />
                  <div className="sidebar-profile-popover__identity">
                    <div className="sidebar-profile-popover__name">{displayName}</div>
                    <div className="sidebar-profile-popover__email">{profileEmailLine}</div>
                    {roleLabel ? (
                      <span
                        className={`sidebar-user-role sidebar-user-role--${getSidebarRoleModifier(derivedRole)} sidebar-user-role--in-popover`}
                        title={roleLabel}
                      >
                        {roleLabel}
                      </span>
                    ) : (
                      <span className="sidebar-profile-popover__role-empty">—</span>
                    )}
                  </div>
                </div>
                <div className="sidebar-profile-popover__divider" />
                <button
                  type="button"
                  className="sidebar-profile-popover__logout"
                  onClick={() => {
                    clearProfilePopoverCloseTimer()
                    setShowProfilePopover(false)
                    setShowLogoutConfirm(true)
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="collapse-indicator" onClick={() => setIsCollapsed(!isCollapsed)}>
          <span className="collapse-line">
            <img
              src="/assets/icons/common/chevron-first.svg"
              alt=""
              className={`nav-icon toggle-icon ${isCollapsed ? "rotated" : ""}`}
            />
          </span>
        </div>
      </aside>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={() => void performLogout()}
        isPending={logoutInProgress}
        title="Log out"
        message="Are you sure you want to log out?"
        confirmButtonLabel="Log out"
        pendingButtonLabel="Logging out…"
      />
    </>
  )
}
